import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Inbox, CheckCircle2, XCircle, Clock, Trash2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';

function contactStatusLabel(s) {
  if (s === 'pending') return { label: 'En attente', class: 'bg-amber-400/10 text-amber-400 border-amber-400/30' };
  if (s === 'reviewed') return { label: 'Traité', class: 'bg-blue-400/10 text-blue-400 border-blue-400/30' };
  if (s === 'approved') return { label: 'Approuvé', class: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/30' };
  if (s === 'rejected') return { label: 'Refusé', class: 'bg-red-400/10 text-red-400 border-red-400/30' };
  return { label: s, class: 'bg-secondary text-muted-foreground' };
}

export { contactStatusLabel };

export function AdminContactsList({ contacts, onStatusChange, onDelete }) {
  if (contacts.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-12 text-center">
          <Inbox className="w-12 h-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
          <p className="text-muted-foreground">Aucune demande pour le moment.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-contacts-list">
      {contacts.map((c) => {
        const statusInfo = contactStatusLabel(c.status);
        return (
          <Card key={c.request_id} className={`bg-card border-border ${c.status === 'pending' ? 'border-l-4 border-l-amber-400' : ''}`} data-testid={`contact-card-${c.request_id}`}>
            <CardContent className="p-5">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-bold text-lg">{c.prenom} {c.nom}</h3>
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full border ${statusInfo.class}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm">
                    <p className="text-muted-foreground">
                      Établissement : <span className="text-foreground">{c.etablissement}</span>
                    </p>
                    <p className="text-muted-foreground">
                      Académie : <span className="text-foreground">{c.academie}</span>
                    </p>
                    <p className="text-muted-foreground">
                      Email : <span className="text-foreground">{c.email}</span>
                    </p>
                    <p className="text-muted-foreground">
                      Date : <span className="text-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</span>
                    </p>
                  </div>
                  {c.message && (
                    <div className="bg-secondary/50 rounded-lg p-3 mt-2">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1 font-bold">Message</p>
                      <p className="text-sm">{c.message}</p>
                    </div>
                  )}
                  {c.reviewed_by && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Traité par {c.reviewed_by} le {c.reviewed_at ? new Date(c.reviewed_at).toLocaleDateString('fr-FR') : ''}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  {c.status === 'pending' ? (
                    <>
                      <Button size="sm" onClick={() => onStatusChange(c.request_id, 'approved')} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" data-testid={`approve-contact-${c.request_id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approuver
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => onStatusChange(c.request_id, 'rejected')} className="border border-border h-8 text-xs hover:text-destructive" data-testid={`reject-contact-${c.request_id}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Refuser
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => onStatusChange(c.request_id, 'pending')} className="h-8 text-xs text-muted-foreground">
                      <Clock className="w-3.5 h-3.5 mr-1" /> Remettre en attente
                    </Button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="h-8 text-xs text-muted-foreground hover:text-destructive" data-testid={`delete-abo-${c.request_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer cette demande ?</AlertDialogTitle>
                        <AlertDialogDescription>La demande de {c.prenom} {c.nom} sera définitivement supprimée.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onDelete(c.request_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`confirm-delete-abo-${c.request_id}`}>Supprimer</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
